import { Box } from '@mui/material';
import { PointerEvent } from 'react';
import { FocusTimeHandle } from '../../../types';
import { DEFAULT_SLOT_TITLE, LABEL_WIDTH, SLOT_HORIZONTAL_OFFSET } from '../layout';
import { FocusTimeDayTimelineSlot } from '../types';
import FocusTimeDaySlotContent from './FocusTimeDaySlotContent';
import FocusTimeDaySlotResizeHandle from './FocusTimeDaySlotResizeHandle';

type FocusTimeDaySlotProps = Readonly<{
  slot: FocusTimeDayTimelineSlot;
  startMinutes: number;
  endMinutes: number;
  top: number;
  height: number;
  isDragging: boolean;
  isStartDisabled: boolean;
  onStartMove: (slot: FocusTimeDayTimelineSlot) => (event: PointerEvent<HTMLDivElement>) => void;
  onMove: (event: PointerEvent<HTMLDivElement>) => void;
  onEndMove: (event: PointerEvent<HTMLDivElement>) => void;
  onRemoveSlot: (slotId: string) => void;
  onStartResize: (slotId: string, handle: FocusTimeHandle) => (event: PointerEvent<HTMLButtonElement>) => void;
  onMoveResize: (slotId: string, handle: FocusTimeHandle) => (event: PointerEvent<HTMLButtonElement>) => void;
  onEndResize: (event: PointerEvent<HTMLButtonElement>) => void;
}>;

const SLOT_SX = {
  position: 'absolute',
  left: LABEL_WIDTH + SLOT_HORIZONTAL_OFFSET,
  right: SLOT_HORIZONTAL_OFFSET + 8,
  zIndex: 2,
  boxSizing: 'border-box',
  overflow: 'visible',
  border: '1px solid var(--mui-palette-border-default)',
  borderRadius: 1,
  backgroundColor: 'var(--mui-palette-background-brandSubtle)',
  color: 'var(--mui-palette-text-primary)',
  boxShadow: '0px 1px 3px 0px var(--mui-palette-border-default)',
  cursor: 'grab',
  userSelect: 'none',
  touchAction: 'none',
  '&:hover [data-focus-time-slot-control], &:focus-within [data-focus-time-slot-control], &[data-focus-time-slot-active="true"] [data-focus-time-slot-control]':
    {
      opacity: 1,
      pointerEvents: 'auto',
      visibility: 'visible',
    },
  '&:hover [data-focus-time-delete-icon], &:focus-within [data-focus-time-delete-icon], &[data-focus-time-slot-active="true"] [data-focus-time-delete-icon]':
    {
      opacity: 1,
      visibility: 'visible',
    },
} as const;

const FocusTimeDaySlot = ({
  slot,
  startMinutes,
  endMinutes,
  top,
  height,
  isDragging,
  isStartDisabled,
  onStartMove,
  onMove,
  onEndMove,
  onRemoveSlot,
  onStartResize,
  onMoveResize,
  onEndResize,
}: FocusTimeDaySlotProps) => {
  const slotTitle = slot.title || DEFAULT_SLOT_TITLE;

  return (
    <Box
      data-focus-time-slot="true"
      onPointerDown={onStartMove(slot)}
      onPointerMove={onMove}
      onPointerUp={onEndMove}
      onPointerCancel={onEndMove}
      data-focus-time-slot-active={isDragging ? 'true' : undefined}
      sx={{
        ...SLOT_SX,
        top: `${top}%`,
        height: `${height}%`,
        zIndex: isDragging ? 4 : 2,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <FocusTimeDaySlotContent
        slotId={slot.id}
        slotTitle={slotTitle}
        startMinutes={startMinutes}
        endMinutes={endMinutes}
        onRemoveSlot={onRemoveSlot}
      />

      <FocusTimeDaySlotResizeHandle
        slotId={slot.id}
        slotTitle={slotTitle}
        handle={FocusTimeHandle.Start}
        position={0}
        disabled={isStartDisabled}
        onStartResize={onStartResize}
        onMoveResize={onMoveResize}
        onEndResize={onEndResize}
      />
      <FocusTimeDaySlotResizeHandle
        slotId={slot.id}
        slotTitle={slotTitle}
        handle={FocusTimeHandle.End}
        position="100%"
        onStartResize={onStartResize}
        onMoveResize={onMoveResize}
        onEndResize={onEndResize}
      />
    </Box>
  );
};

export default FocusTimeDaySlot;
